import { apiRequest } from "./api";

export type LoanStatus = "PENDING" | "APPROVED" | "ACTIVE" | "REJECTED" | "PAID";
export type InstallmentStatus = "PENDING" | "DEDUCTED";

export interface Loan {
  loanId: string;
  userId: string;
  businessId?: string;
  employeeName?: string;
  amount: number;
  termPeriods: number;
  reason: string;
  status: LoanStatus;
  approvedBy?: string;
  reviewedAt?: string;
  rejectedReason?: string;
  releaseDate?: string;
  disbursementAccount?: string;
  firstInstallmentPeriod?: string;
  lastInstallmentPeriod?: string;
  installmentAmount?: number;
  interestRate?: number;
  settledInstallments?: number;
  totalInstallments?: number;
  createdAt: string;
  updatedAt: string;
}

export interface LoanInstallment {
  loanId: string;
  seq: number;
  duePeriod: string;
  amount: number;
  status: InstallmentStatus;
}

export interface ApplyLoanPayload {
  businessId: string;
  amount: number;
  termPeriods: number;
  reason: string;
  employeeName?: string;
}

export interface ListLoansResponse {
  items: Loan[];
  nextKey: string | null;
}

export interface LoanConfig {
  businessId: string;
  interestRate: number;
  allowedTerms: number[];
  maxLoanableAmount?: number;
  disbursementAccounts?: { id: string; name: string }[];
  updatedAt: string;
  updatedBy: string;
}

export type SaveLoanConfigInput = Pick<LoanConfig, "interestRate" | "allowedTerms" | "maxLoanableAmount" | "disbursementAccounts">;

export const loanConfigApi = {
  get: (businessId: string) =>
    apiRequest<{ config: LoanConfig | null }>(
      `/biz/${encodeURIComponent(businessId)}/loans/config`,
    ),
  save: (businessId: string, input: SaveLoanConfigInput) =>
    apiRequest<{ config: LoanConfig }>(
      `/biz/${encodeURIComponent(businessId)}/loans/config`,
      { method: "PUT", body: input },
    ),
};

export const loanApi = {
  apply: (payload: ApplyLoanPayload) =>
    apiRequest<Loan>("/me/loans", { method: "POST", body: payload }),
  listMine: (status?: LoanStatus) =>
    apiRequest<{ items: Loan[] }>("/me/loans", { params: status ? { status } : undefined }),
  getMine: (loanId: string) =>
    apiRequest<{ loan: Loan; installments: LoanInstallment[] }>(`/me/loans/${loanId}`),
  adminList: (businessId: string, status = "PENDING", limit = 50, lastKey?: string) =>
    apiRequest<ListLoansResponse>("/admin/loans", {
      params: { businessId, status, limit, lastKey },
    }),
  approve: (loanId: string, userId: string, businessId: string) =>
    apiRequest<{ success: boolean }>(`/admin/loans/${loanId}/approve`, {
      method: "POST",
      body: { userId, businessId },
    }),
  reject: (loanId: string, userId: string, businessId: string, reason: string) =>
    apiRequest<{ success: boolean }>(`/admin/loans/${loanId}/reject`, {
      method: "POST",
      body: { userId, businessId, reason },
    }),
  release: (loanId: string, userId: string, businessId: string, releaseDate: string, disbursementAccount?: string) =>
    apiRequest<{ success: boolean }>(`/admin/loans/${loanId}/release`, {
      method: "POST",
      body: { userId, businessId, releaseDate, ...(disbursementAccount ? { disbursementAccount } : {}) },
    }),
};
